import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/lib/auth";

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: {
            client_id: string;
            callback: (response: { credential: string }) => void;
          }) => void;
          renderButton: (parent: HTMLElement, options: Record<string, unknown>) => void;
        };
      };
    };
  }
}

export default function Login() {
  const { loginWithGoogle } = useAuth();
  const buttonRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.onload = () => {
      if (!window.google || !buttonRef.current) return;
      window.google.accounts.id.initialize({
        client_id: import.meta.env.VITE_GOOGLE_CLIENT_ID,
        callback: async (response) => {
          try {
            await loginWithGoogle(response.credential);
          } catch {
            setError("Sign-in didn't go through. Please try again.");
          }
        },
      });
      window.google.accounts.id.renderButton(buttonRef.current, {
        theme: "filled_black",
        size: "large",
        shape: "pill",
        width: 280,
      });
    };
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, [loginWithGoogle]);

  return (
    <div className="flex min-h-dvh flex-col items-center justify-center gap-8 bg-canvas px-6 text-center">
      <div>
        <h1 className="text-3xl font-semibold text-forest-700">PregTrack</h1>
        <p className="mt-2 max-w-xs text-sm text-forest-500">
          Simple, supportive nutrition tracking for every week of your pregnancy.
        </p>
      </div>
      <div ref={buttonRef} />
      {error && <p className="text-sm text-clay-500">{error}</p>}
      <p className="max-w-xs text-xs text-forest-300">
        PregTrack supports your nutrition tracking — it doesn't diagnose. Always follow guidance
        from your OB/GYN or midwife.
      </p>
    </div>
  );
}
